<template>
  <chat-message :prev-chat="prevChat" :chat="chat" @delete="$emit('delete', chat)">
    <div v-if="isYesNoQuestion" class="chat-message__actions flex">
      <theme-button
        v-for="(action, index) in chat.message_rich.actions"
        :key="index"
        outline
        :class="['w-1/2', { 'ml-2': index > 0 }]"
        style="min-width: 80px"
        :selected="action.selected"
        :disabled="!action.selected && hasSelected"
        @click="clickAction(action)"
        >{{ action.text }}</theme-button
      >
    </div>
    <ul
      v-else-if="
        chat.message_rich.auto_message_info &&
        chat.message_rich.auto_message_info.menu_style === 'compact'
      "
      class="chat-message__actions-compact"
    >
      <action
        v-for="(action, index) in chat.message_rich.actions"
        :key="index"
        :action="action"
        :index="index"
        :check-action-state="checkActionState"
        :click-action="clickAction"
      />
    </ul>
    <ul v-else class="chat-message__actions">
      <action
        v-for="(action, index) in chat.message_rich.actions"
        :key="index"
        :action="action"
        :index="index"
        :check-action-state="checkActionState"
        :click-action="clickAction"
      />
    </ul>
  </chat-message>
</template>

<script>
import { formatChatMessage } from 'Utilities'
import { Chat } from 'src/apis'
import { EventBus } from 'ChatWidget/EventBus'
import Action from './Action.vue'
import getCurrentLocation from 'Utilities/Geolocation'
import { initialHourIncrement, initialMinuteIncrement, initialTime } from 'javascript/Constants'
import { getRoundTime } from 'Utilities/TimerHelper'
import { openBookingWidget } from 'Utilities/openBookingWidget'
import _isInteger from 'lodash/isInteger'
import ChatMessage from 'Chats/ChatMessage'
import chatWidgetMixin from 'Chats/mixins/chatWidgetMixin'
import ThemeButton from 'javascript/Common/ThemeButton'
import { useAppStore } from 'Store/app'
import { useBottomPanelStore } from 'Store/bottom-panel'
import { useSettingsStore } from 'Store/settings'
import { useChatStore } from 'Store/chat'
import { useDatepickerStore } from 'Store/datepicker'
import { useDetailsPageStore } from 'Store/details-page'
import { showLoginView } from 'javascript/helpers/login'

export default {
  components: {
    ThemeButton,
    ChatMessage,
    Action
  },
  mixins: [chatWidgetMixin],

  props: {
    openUserPanel: Function
  },

  data() {
    return {
      appStore: null,
      bottomPanelStore: null,
      settingsStore: null,
      chatStore: null,
      datepickerStore: null,
      detailsPageStore: null,
      active: this.chat.message_rich.hasOwnProperty('active')
        ? this.chat.message_rich.active
        : true,
      actions: this.chat.message_rich.actions
    }
  },

  computed: {
    formatChatMessage() {
      return formatChatMessage(this.chat.message_rich.message)
    },

    hasSelected() {
      return this.chat.message_rich.actions.some((action) => action.selected)
    },

    isYesNoQuestion() {
      return (
        this.chat.message_rich?.auto_message_info?.type === 'question' ||
        this.chat.message_rich?.actions?.some(
          (action) => action.sns_bot_step === 'collect_user_input_question_yes_no'
        )
      )
    }
  },

  created() {
    this.appStore = useAppStore()
    this.bottomPanelStore = useBottomPanelStore()
    this.settingsStore = useSettingsStore()
    this.chatStore = useChatStore()
    this.datepickerStore = useDatepickerStore()
    this.detailsPageStore = useDetailsPageStore()
  },
  mounted() {
    // disable this widget on datepicker send date
    EventBus.$on('pageDisabledActionWidget', () => {
      if (this.chat.id === this.detailsPageStore.chat.id) {
        this.active = false
      }
    })
  },
  methods: {
    jumpToState(step, restartStepId, currentStepId) {
      if (step || _isInteger(restartStepId) || _isInteger(currentStepId)) {
        Chat.goToState(this.chatStore.chatId, step, restartStepId, currentStepId)
      }
    },
    sendAction(action) {
      if (action.action) {
        this.active = false
        this.$set(action, 'selected', true)
        if (action.action === 'languages') {
          EventBus.$emit('show-language-menu', false)
          return
        }
        const options = {
          messageID: this.chat.id,
          messageAction: action.action,
          messageActionId: action.action_id,
          resetValue: action.reset
        }
        if (this.chat.message_rich.auto_message_info) {
          options.autoMessageType = this.chat.message_rich.auto_message_info.type
        }
        Chat.sendMessage(action.text, options)
      }
    },
    clickAction(action) {
      if (
        (!this.active && this.chat.message_rich.auto_message_info.type !== 'menu') ||
        action.selected
      )
        return

      if (
        this.chat.message_rich?.auto_message_info?.type === 'menu' &&
        action.action === 'room_reservation'
      ) {
        this.$gtag.recordChatMenuActionRoomReservationTriggered(action)
      }

      switch (this.chat.message_rich.type) {
        // FIXME https://umami-me.atlassian.net/browse/CON-3739
        case 'auto_message':
          if (
            this.chat.message_rich.auto_message_info &&
            this.chat.message_rich.auto_message_info.type === 'auth'
          ) {
            showLoginView()
            this.appStore.setAutoMessageAuthId(
              this.chat.message_rich.auto_message_info.successful_auth_auto_message_id ||
                this.chat.id
            )
            this.appStore.setAutoMessageAuthType('action_auto_message')
          } else {
            this.sendAction(action)
          }
          break
        case 'time':
          let defaultTime = action.default_time

          if (this.chat.message_rich.enabled_times) {
            defaultTime = this.chat.message_rich.enabled_times[0]
          } else {
            defaultTime = initialTime
          }

          const hourIncrement = action.hour_increment || initialHourIncrement
          const minuteIncrement = action.minute_increment || initialMinuteIncrement
          this.bottomPanelStore.setBottomPanelComponent('TimePickerPanel')
          this.datepickerStore.setTimeSettings({
            currentTime: getRoundTime(defaultTime, hourIncrement, minuteIncrement),
            defaultHourIncrement: hourIncrement,
            defaultMinuteIncrement: minuteIncrement,
            enabled_times: this.chat.message_rich.enabled_times
          })
          break
        case 'operator':
          this.active = false
          this.$set(action, 'selected', true)
          Chat.callOperator(this.chat.id, action.action, action.user_chat_message_id)
          break
        case 'form_action':
          this.chatStore.initForm(action.page, this.chat)
          this.jumpToState(action.step)
          this.sendAction(action)
          break
        case 'booking_widget':
          this.appStore.setAutoMessageAuthId(
            this.chat.message_rich.auto_message_info?.successful_auth_auto_message_id ||
              this.chat.id
          )
          this.appStore.setAutoMessageAuthType('action_auto_message')
          openBookingWidget(this.chat.message_rich.booking_widget_params)
          break
        default:
          // We will send the chat message ID and action back to the backend
          // The message ID is to disable the action widget in the future
          // The action is for serverflow (that cannot always understand the text and needs the index)
          if (action.url || action.phone) return
          this.active = false
          this.$set(action, 'selected', true)

          // if action is do_nothing then.. well.. do nothing
          // give chat message input focus THEN do nothing
          if (action.action === 'do_nothing') {
            EventBus.$emit('focus-text-message')
            return
          }

          if (
            action.step ||
            _isInteger(action.restart_step_id) ||
            _isInteger(action.current_step_id)
          ) {
            this.jumpToState(action.step, action.restart_step_id, action.current_step_id)
            return
          }

          if (action.action === 'directions' || action.action === 'current_location') {
            getCurrentLocation().then((position) => {
              const options = {
                messageID: this.chat.id,
                messageAction: action.action,
                ...position
              }
              Chat.sendMessage(action.text, options)
            })
          } else if (action.action && action.action.toString().match(/^closing_/)) {
            Chat.sendClosingResponse(this.chat.id, action.action)
          } else if (action.action === 'call_operator') {
            // quick_reply widget that calls operator
            Chat.callOperator(this.chat.id, 'yes', action.text, action.user_chat_message_id)
          } else if (action.action === 'nlp_feedback') {
            // quick_reply widget to rate dialogflow response
            Chat.nlpFeedback(action.request, action.is_correct)
          } else {
            const options = {
              messageID: this.chat.id,
              messageAction: action.action,
              resetValue: action.reset,
              autoMessageFaqCode: action.auto_message,
              useAi: action.use_ai
            }
            Chat.sendMessage(action.text, options)
          }
      }
    },
    checkActionState(index) {
      if (!this.actions) return ''

      if (this.actions[index].selected === true) {
        return 'choosen'
      }
      if (this.active === false) {
        return 'inactive'
      } else {
        return ''
      }
    }
  }
}
</script>
